(() => {
  const railSelector = '[data-author-focus]';
  const bookSelector = '[data-author-focus-book]';
  const selectSelector = '[data-author-focus-select]';
  const responseCache = new Map();

  const parseConfig = (element) => {
    const script = element.querySelector('[data-author-focus-config]');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return {};
    }
  };

  const fetchResponseText = async (url) => {
    if (!url) return '';
    if (responseCache.has(url)) return responseCache.get(url);

    const request = fetch(url, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error('Metadata Hero book request failed');
        return response.text();
      })
      .catch(() => '');

    responseCache.set(url, request);
    return request;
  };

  const booksFromResponse = (html) => {
    if (!html) return [];

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(documentFragment.querySelectorAll(bookSelector));
  };

  const productKey = (book) => book.dataset.productId || book.dataset.productHandle || '';

  const dedupeBooks = (books) => {
    const seen = new Set();

    return books.filter((book) => {
      const key = productKey(book);
      if (!key) return true;
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  };

  const relationshipPriority = (relationship) => {
    const normalized = String(relationship || '').toLowerCase().trim();

    if (normalized === 'ordered') return 0;
    if (normalized === 'companion') return 2;

    return 1;
  };

  const parseSeriesNumber = (book) => {
    const number = Number.parseFloat(book.dataset.seriesNumber);
    return Number.isFinite(number) ? number : null;
  };

  const hasSeriesOrdinal = (book) =>
    String(book.dataset.seriesRelationship || '').toLowerCase().trim() === 'ordered' &&
    parseSeriesNumber(book) !== null;

  const isAvailable = (book) => book.dataset.available === 'true';

  const groupByWorkId = (books, currentWorkId) => {
    const groups = new Map();

    books.forEach((book) => {
      const workId = book.dataset.workId;

      if (!workId) return;
      if (currentWorkId && workId === currentWorkId) return;

      if (!groups.has(workId)) groups.set(workId, []);
      groups.get(workId).push(book);
    });

    return groups;
  };

  const chooseRepresentative = (books) => books.find(isAvailable) || books[0];

  const sortSeriesBooks = (books) =>
    [...books].sort((a, b) => {
      const relationshipDifference =
        relationshipPriority(a.dataset.seriesRelationship) -
        relationshipPriority(b.dataset.seriesRelationship);

      if (relationshipDifference !== 0) return relationshipDifference;

      const aSeriesNumber = parseSeriesNumber(a);
      const bSeriesNumber = parseSeriesNumber(b);
      const aHasSeriesNumber = aSeriesNumber !== null;
      const bHasSeriesNumber = bSeriesNumber !== null;

      if (aHasSeriesNumber !== bHasSeriesNumber) return aHasSeriesNumber ? -1 : 1;
      if (aHasSeriesNumber && bHasSeriesNumber && aSeriesNumber !== bSeriesNumber) {
        return aSeriesNumber - bSeriesNumber;
      }

      return String(a.dataset.productTitle || '').localeCompare(String(b.dataset.productTitle || ''));
    });

  const prepareSeriesBooks = (books, config) => {
    const initialProductId = String(config.initialProductId || '');
    const initialBook = books.find(
      (book) => String(book.dataset.productId) === initialProductId
    );
    const currentWorkId = String(config.initialWorkId || initialBook?.dataset.workId || '');
    const showUnavailable = config.showUnavailable !== false;
    const productLimit = Math.max(Number(config.productLimit) || 10, 1);
    const eligibleBooks = books.filter(
      (book) =>
        showUnavailable ||
        isAvailable(book) ||
        String(book.dataset.productId) === initialProductId
    );
    const groups = groupByWorkId(eligibleBooks, currentWorkId);
    const representatives = Array.from(groups.values())
      .map(chooseRepresentative)
      .filter(Boolean);
    const sortedBooks = sortSeriesBooks(
      initialBook ? [initialBook, ...representatives] : representatives
    );
    let limitedBooks = sortedBooks.slice(0, productLimit);

    if (initialBook && !limitedBooks.includes(initialBook)) {
      limitedBooks = sortSeriesBooks([
        ...limitedBooks.slice(0, Math.max(productLimit - 1, 0)),
        initialBook
      ]);
    }

    const lowestOrdinalBook = limitedBooks.find(hasSeriesOrdinal);
    const centeredBook = lowestOrdinalBook || initialBook || limitedBooks[0];

    return {
      books: limitedBooks,
      centeredProductId: String(centeredBook?.dataset.productId || '')
    };
  };

  class AuthorFocusRail {
    constructor(element) {
      this.element = element;
      this.track = element.querySelector('[data-author-focus-track]');
      this.status = element.querySelector('[data-author-focus-status]');
      this.previousControl = element.querySelector('[data-author-focus-previous]');
      this.nextControl = element.querySelector('[data-author-focus-next]');
      this.config = parseConfig(element);
      this.books = [];
      this.currentIndex = 0;
      this.timer = null;
      this.destroyed = false;
      this.listeners = new AbortController();
      this.detailsFrame = null;
      this.detailsWidth = null;
      this.detailSources = [];
      this.detailsObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (width !== this.observedWidth) this.detailsWidth = null;
        this.observedWidth = width;
        this.scheduleDetailsHeight();
      });
      this.invalidateDetailsHeight = () => {
        this.detailsWidth = null;
        this.scheduleDetailsHeight();
      };
      this.contentObserver = new MutationObserver(this.invalidateDetailsHeight);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoRotate =
        this.config.autoRotate === true &&
        !this.reducedMotion &&
        !window.Shopify?.designMode;
      this.rotationInterval = Math.max(Number(this.config.rotationInterval) || 6000, 3000);

      if (!this.track) return;

      this.onTrackClick = this.onTrackClick.bind(this);
      this.onPreviousClick = this.onPreviousClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);

      const options = { signal: this.listeners.signal };
      this.track.addEventListener('click', this.onTrackClick, options);
      this.previousControl?.addEventListener('click', this.onPreviousClick, options);
      this.nextControl?.addEventListener('click', this.onNextClick, options);
      this.element.addEventListener('keydown', this.onKeyDown, options);
      this.element.addEventListener('mouseenter', this.onMouseEnter, options);
      this.element.addEventListener('mouseleave', this.onMouseLeave, options);
      this.element.addEventListener('focusin', this.onFocusIn, options);
      this.element.addEventListener('focusout', this.onFocusOut, options);
      document.addEventListener('visibilitychange', this.onVisibilityChange, options);
      window.addEventListener('resize', this.invalidateDetailsHeight, options);
      document.fonts?.addEventListener('loadingdone', this.invalidateDetailsHeight, options);

      this.onSectionUnload = (event) => {
        if (!event.target.contains(this.element)) return;
        this.destroy();
      };
      document.addEventListener('shopify:section:unload', this.onSectionUnload, options);
      this.populate();
    }

    async populate() {
      const fallbackBooks = Array.from(this.track.querySelectorAll(bookSelector));
      const responseText = await fetchResponseText(this.config.fetchUrl);
      if (this.destroyed) return;
      const fetchedBooks = booksFromResponse(responseText);
      const initialProductId = String(this.config.initialProductId || '');
      const showUnavailable = this.config.showUnavailable !== false;
      const productLimit = Math.max(Number(this.config.productLimit) || 10, 1);
      const candidateBooks = dedupeBooks([...fallbackBooks, ...fetchedBooks]);
      let centeredProductId = initialProductId;
      let books;

      if (this.config.orderMode === 'series') {
        const seriesResult = prepareSeriesBooks(candidateBooks, this.config);
        books = seriesResult.books;
        centeredProductId = seriesResult.centeredProductId;
      } else {
        books = candidateBooks
          .filter(
            (book) =>
              showUnavailable ||
              book.dataset.available === 'true' ||
              String(book.dataset.productId) === initialProductId
          )
          .slice(0, productLimit);
      }

      if (books.length === 0) {
        this.element.hidden = true;
        return;
      }

      this.track.replaceChildren(...books);
      this.books = books;
      this.detailSources = books.map((book) =>
        book.querySelector('.homepage-hero-author-focus__book-details')
      ).filter(Boolean);
      this.detailSources.forEach((details) => {
        this.contentObserver.observe(details, {
          subtree: true, childList: true, characterData: true, attributes: true
        });
      });
      this.currentIndex = Math.max(
        books.findIndex((book) => String(book.dataset.productId) === centeredProductId),
        0
      );
      this.element.dataset.bookCount = String(books.length);
      const coverRatios = books.map((book) => Number.parseFloat(
        book.querySelector('.homepage-hero-author-focus__cover-media')?.style.getPropertyValue('--author-cover-ratio')
      )).filter((ratio) => Number.isFinite(ratio) && ratio > 0);
      if (coverRatios.length) {
        // The widest image sets a height that every image can fill without cropping.
        // Portrait covers then become narrower, rather than taller than their neighbors.
        const widestRatio = Math.max(...coverRatios);
        this.element.style.setProperty('--author-rail-cover-ratio', String(widestRatio));
        this.element.dataset.coverShape = widestRatio >= 0.85 ? 'wide' : 'portrait';
      }
      this.element.hidden = false;
      this.render();
      this.detailsObserver.observe(this.element);
      document.fonts?.ready.then(this.invalidateDetailsHeight);
      this.resume();
    }

    onTrackClick(event) {
      const selectButton = event.target.closest(selectSelector);
      if (!selectButton) return;

      const book = selectButton.closest(bookSelector);
      const index = this.books.indexOf(book);
      if (index < 0) return;

      if (index === this.currentIndex) {
        const productUrl = book.dataset.productUrl;
        if (productUrl) window.location.assign(productUrl);
        return;
      }

      this.select(index, { announce: true, restart: true });
    }

    onPreviousClick() {
      this.select(this.currentIndex - 1, { announce: true, restart: true });
    }

    onNextClick() {
      this.select(this.currentIndex + 1, { announce: true, restart: true });
    }

    onKeyDown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (event.target.closest('input, select, textarea')) return;

      event.preventDefault();
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      this.select(this.currentIndex + direction, {
        announce: true,
        focus: true,
        restart: true
      });
    }

    onMouseEnter() {
      this.pause();
    }

    onMouseLeave() {
      this.resume();
    }

    onFocusIn() {
      this.pause();
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (!this.destroyed && !this.element.contains(document.activeElement)) this.resume();
      }, 0);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    }

    select(index, options = {}) {
      const count = this.books.length;
      if (count === 0) return;

      this.currentIndex = (index + count) % count;
      this.render();

      const activeBook = this.books[this.currentIndex];

      if (options.announce && this.status) {
        this.status.textContent = activeBook.dataset.productTitle || '';
      }

      if (options.focus) {
        window.requestAnimationFrame(() => {
          if (!this.destroyed) activeBook.querySelector(selectSelector)?.focus();
        });
      }

      if (options.restart) this.restart();
    }

    render() {
      const count = this.books.length;
      if (count === 0) return;

      this.currentIndex = (this.currentIndex + count) % count;
      const visibleRadius = count >= 5 ? 2 : 1;

      this.books.forEach((book, index) => {
        const offset = this.relativeOffset(index, count);
        const isActive = offset === 0;
        const selectButton = book.querySelector(selectSelector);

        book.hidden = Math.abs(offset) > visibleRadius;
        book.classList.toggle('is-active', isActive);
        book.classList.toggle('is-before', offset === -1);
        book.classList.toggle('is-after', offset === 1);
        book.classList.toggle('is-far-before', offset === -2);
        book.classList.toggle('is-far-after', offset === 2);

        if (!selectButton) return;

        const label = isActive ? selectButton.dataset.viewLabel : selectButton.dataset.selectLabel;
        selectButton.setAttribute('aria-pressed', String(isActive));
        if (label) selectButton.setAttribute('aria-label', label);
      });

      this.scheduleDetailsHeight();
    }

    scheduleDetailsHeight() {
      if (this.destroyed || this.detailsFrame !== null) return;
      this.detailsFrame = window.requestAnimationFrame(() => {
        this.detailsFrame = null;
        this.syncDetailsHeight();
      });
    }

    syncDetailsHeight() {
      const details = this.books[this.currentIndex]?.querySelector('.homepage-hero-author-focus__book-details');
      const width = details?.offsetWidth;
      if (!width || this.detailsWidth === width) return;

      // Measure every book at the same untransformed width, including off-stage books.
      // Insert all clones before reading layout, avoiding a layout pass per book.
      const rowHeights = { title: 0, price: 0, action: 0 };
      const fragment = document.createDocumentFragment();
      const measurements = this.detailSources.map((source) => {
        const measure = source.cloneNode(true);
        measure.dataset.authorMeasuring = '';
        measure.setAttribute('aria-hidden', 'true');
        measure.inert = true;
        measure.style.width = `${width}px`;
        measure.removeAttribute('id');
        measure.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        fragment.append(measure);
        return measure;
      });
      details.parentElement.append(fragment);
      measurements.forEach((measure) => {
        Object.keys(rowHeights).forEach((row) => {
          const child = measure.querySelector(`[data-author-focus-${row}]`);
          rowHeights[row] = Math.max(rowHeights[row], child?.offsetHeight || 0);
        });
      });
      measurements.forEach((measure) => measure.remove());
      Object.entries(rowHeights).forEach(([row, height]) => {
        this.element.style.setProperty(`--author-${row}-height`, `${height}px`);
      });
      const height = details?.offsetHeight;
      if (!height) return;
      this.element.style.setProperty('--author-active-details-height', `${height}px`);
      // Rotation shares these reserved rows. Remeasure only after size/content/font changes.
      this.detailsWidth = width;
    }

    destroy() {
      this.destroyed = true;
      this.pause();
      this.listeners.abort();
      this.detailsObserver.disconnect();
      this.contentObserver.disconnect();
      window.cancelAnimationFrame(this.detailsFrame);
      delete this.element.dataset.authorFocusInitialized;
    }

    relativeOffset(index, count) {
      let offset = index - this.currentIndex;
      const midpoint = Math.floor(count / 2);

      if (offset > midpoint) offset -= count;
      if (offset < -midpoint) offset += count;

      return offset;
    }

    pause() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    resume() {
      if (
        this.destroyed ||
        !this.autoRotate ||
        this.timer ||
        document.hidden ||
        this.books.length < 2 ||
        this.element.matches(':hover') ||
        this.element.contains(document.activeElement)
      ) {
        return;
      }

      this.timer = window.setInterval(() => {
        this.select(this.currentIndex + 1);
      }, this.rotationInterval);
    }

    restart() {
      this.pause();
      this.resume();
    }
  }

  const initialize = (scope = document) => {
    const rails = Array.from(scope.querySelectorAll?.(railSelector) || []);
    if (scope.matches?.(railSelector)) rails.unshift(scope);

    rails.forEach((rail) => {
      if (rail.dataset.authorFocusInitialized === 'true') return;

      rail.dataset.authorFocusInitialized = 'true';
      new AuthorFocusRail(rail);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
